import type { IAgentHandleRecord } from "./agentAnswer.types";

/**
 * One tool call as the server logged it. Inputs and timings only — the server
 * never sends results, so nothing derived from a tool's output can come from
 * here.
 */
export interface IAgentToolLogEntry {
    tool: string;
    input?: Record<string, unknown>;
    ms?: number | null;
    /** Rows returned, when the tool was a record lister. */
    n?: number | null;
    /** Rows matching before any limit, when the tool reports it. */
    total_count?: number | null;
}

/** The `final` event: the same envelope `/agent/send` returns. */
export interface IAgentFinalEnvelope {
    text: string;
    done: boolean;
    timed_out: boolean;
    session_id: string;
    engine: string;
    /**
     * This turn's records, keyed by the `rN` the prose cites.
     *
     * Turn-scoped, despite the server keeping a session-scoped map under the
     * same name: the envelope carries only handles minted during this turn, so
     * a caller rendering a transcript has to accumulate them itself.
     */
    handles: Record<string, IAgentHandleRecord>;
    tool_log: IAgentToolLogEntry[];
    messages: string[];
}

export type AgentStreamEvent =
    | { type: "tool_start"; tool: string; input: Record<string, unknown>; call_id: string }
    | { type: "tool_end"; tool: string; call_id: string; ms: number | null }
    | { type: "text"; text: string }
    | { type: "final"; envelope: IAgentFinalEnvelope }
    | { type: "error"; status: number; detail: string };

/**
 * Reads a string field off a parsed frame.
 *
 * Only accepts an actual string: `String(value)` on an object yields
 * "[object Object]", which would reach the user as a tool name or as answer
 * text rather than failing.
 */
function text(payload: Record<string, unknown>, key: string, fallback = ""): string {
    const raw = payload[key];
    return typeof raw === "string" ? raw : typeof raw === "number" ? String(raw) : fallback;
}

/**
 * Parses one SSE frame into an event, or null if it is not one we act on.
 *
 * Per frame, not per line: carrying an event name across frames mislabels the
 * payload after a dataless frame, such as `sse_starlette`'s keepalive comment.
 */
function parseFrame(frame: string): AgentStreamEvent | null {
    let name = "";
    const dataLines: string[] = [];

    for (const line of frame.split("\n")) {
        if (line.startsWith(":")) {
            // A comment. The keepalive is one of these.
            continue;
        }
        if (line.startsWith("event:")) {
            name = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trimStart());
        }
    }

    if (name.length === 0 || dataLines.length === 0) {
        return null;
    }

    let payload: Record<string, unknown>;
    try {
        payload = JSON.parse(dataLines.join("\n")) as Record<string, unknown>;
    } catch {
        return null;
    }

    switch (name) {
        case "tool_start":
            return {
                type: "tool_start",
                tool: text(payload, "tool"),
                input:
                    payload.input !== null && typeof payload.input === "object"
                        ? (payload.input as Record<string, unknown>)
                        : {},
                call_id: text(payload, "call_id"),
            };
        case "tool_end":
            return {
                type: "tool_end",
                tool: text(payload, "tool"),
                call_id: text(payload, "call_id"),
                ms: typeof payload.ms === "number" ? payload.ms : null,
            };
        case "text":
            return { type: "text", text: text(payload, "text") };
        case "final":
            return { type: "final", envelope: payload as unknown as IAgentFinalEnvelope };
        case "error":
            return {
                type: "error",
                status: typeof payload.status === "number" ? payload.status : 0,
                detail: text(payload, "detail", "stream error"),
            };
        default:
            return null;
    }
}

/**
 * Reads an SSE response body and yields the events in order.
 *
 * Hand-rolled rather than `EventSource` because the route is a POST with a
 * bearer token, which `EventSource` cannot send.
 */
export async function* readAgentStream(
    response: Response,
): AsyncGenerator<AgentStreamEvent, void, void> {
    if (!response.body) {
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        // \r\n\r\n as well as \n\n: sse_starlette's separator is configurable
        // and the proxy in front of it is free to rewrite line endings.
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

        let split = buffer.indexOf("\n\n");
        while (split !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            const event = parseFrame(frame);
            if (event !== null) {
                yield event;
            }
            split = buffer.indexOf("\n\n");
        }
    }

    // A last frame with no trailing blank line still counts.
    const trailing = parseFrame(buffer);
    if (trailing !== null) {
        yield trailing;
    }
}
